import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Copy, Crown, Shield } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Hero } from "@/components/Hero";
import { PurchaseMarquee } from "@/components/PurchaseMarquee";
import { PackageCard } from "@/components/PackageCard";
import { PackageDetailModal } from "@/components/PackageDetailModal";
import { UsernameDialog } from "@/components/UsernameDialog";

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [donators, setDonators] = useState([]);
  const [goal, setGoal] = useState(null);
  const [pending, setPending] = useState(null);
  const [viewing, setViewing] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/packages", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
    api.get("/donations/top").then((r) => setDonators(r.data)).catch(() => {});
    api.get("/goal").then((r) => setGoal(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />
      <PurchaseMarquee />

      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-28">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <p className="text-[11px] font-semibold tracking-[0.35em] text-[#A78BFA]">01 — LEGENDARY LOOT</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-white">
              Forged for the <span className="italic text-[#A78BFA]">relentless</span>
            </h2>
          </div>
          <Link
            to="/store"
            data-testid="featured-view-all-link"
            className="group inline-flex items-center gap-2 text-sm font-bold tracking-[0.15em] text-slate-400 hover:text-[#A78BFA] transition-colors duration-300"
          >
            VIEW ALL PACKAGES
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {featured.map((pkg, i) => (
            <PackageCard key={pkg.id} pkg={pkg} index={i} onPurchase={setPending} compact onView={setViewing} />
          ))}
        </div>
      </section>

      <section className="relative border-t border-white/10 overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(139,92,246,0.1), transparent 70%)" }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-28">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-center"
          >
            <p className="text-[11px] font-semibold tracking-[0.35em] text-[#A78BFA]">02 — HALL OF LEGENDS</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-white">
              This month's <span className="italic text-[#A78BFA]">top supporters</span>
            </h2>
          </motion.div>

          {goal && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-12 rounded-2xl border border-white/10 bg-[#0D0B12]/80 p-6"
              data-testid="goal-section"
            >
              <div className="flex items-center justify-between text-xs font-bold tracking-[0.2em]">
                <span className="text-slate-400">MONTHLY COMMUNITY GOAL</span>
                <span className="text-slate-300 font-mono" data-testid="goal-progress-text">
                  ${goal.current.toFixed(2)} / ${goal.target.toFixed(2)}
                </span>
              </div>
              <div className="mt-3 h-3 rounded-full bg-white/5 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#C4B5FD] shadow-[0_0_20px_rgba(139,92,246,0.5)]"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${Math.min(100, (goal.current / goal.target) * 100)}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                  data-testid="goal-progress-bar"
                />
              </div>
              <p className="mt-3 text-[11px] text-slate-500">
                {goal.current >= goal.target
                  ? "Goal reached — the realm thanks you."
                  : `${Math.max(0, Math.round(100 - (goal.current / goal.target) * 100))}% to go. Every donation fills the bar.`}
              </p>
            </motion.div>
          )}

          {donators.length === 0 ? (
            <p className="mt-14 text-center text-slate-500 text-sm" data-testid="donators-empty">
              No legends yet this month — be the first.
            </p>
          ) : (
            <div className="mt-14 space-y-3" data-testid="donators-board">
              {donators.map((d, i) => (
                <motion.div
                  key={d.username}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`donator-row-${d.username}`}
                  className={`flex items-center gap-5 rounded-2xl border px-6 py-5 ${
                    i === 0
                      ? "border-[#8B5CF6]/50 bg-[#8B5CF6]/10 shadow-[0_0_40px_rgba(139,92,246,0.15)]"
                      : "border-white/10 bg-[#0D0B12]/80"
                  }`}
                >
                  <span className={`font-display text-3xl w-10 ${i === 0 ? "text-[#A78BFA]" : "text-slate-600"}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {i === 0 && <Crown className="w-5 h-5 text-amber-400 shrink-0" />}
                  <span className="flex-1 font-semibold text-white truncate">{d.username}</span>
                  <span className="font-mono text-[#A78BFA]">${d.total.toFixed(2)}</span>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="relative border-t border-white/10 overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 55% 70% at 50% 100%, rgba(139,92,246,0.18), transparent 70%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 py-28 text-center">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-[11px] font-semibold tracking-[0.35em] text-[#A78BFA]">03 — JOIN THE REALM</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">
              Your legacy starts <span className="italic text-[#A78BFA]">tonight</span>
            </h2>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate("/store")}
                data-testid="cta-enter-store-button"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-bold tracking-[0.15em] shadow-[0_0_40px_rgba(139,92,246,0.4)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5"
              >
                ENTER THE STORE <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText("ULTIsmp.net").catch(() => {});
                  toast.success("Server IP copied", { description: "ULTIsmp.net" });
                }}
                data-testid="cta-copy-ip-button"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#8B5CF6]/50 text-white text-sm font-bold tracking-[0.15em] transition-[background-color,border-color] duration-300"
              >
                <Copy className="w-4 h-4" /> COPY SERVER IP
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-[#0D0B12]/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#8B5CF6]" strokeWidth={2.5} />
            <span className="font-display text-lg tracking-tight text-white">ULTI<span className="text-[#A78BFA]">SMP</span></span>
          </div>
          <div className="flex items-center gap-6 text-xs text-slate-500 font-mono">
            <span data-testid="footer-java-ip">Java — ULTIsmp.net</span>
            <span data-testid="footer-bedrock-ip">Bedrock — ULTIsmp.net:19132</span>
          </div>
          <p className="text-[11px] text-slate-600 text-center">Not affiliated with Mojang or Microsoft. Demo checkout — no real payments.</p>
        </div>
      </footer>

      <UsernameDialog pkg={pending} onClose={() => setPending(null)} />
      <PackageDetailModal
        pkg={viewing}
        onClose={() => setViewing(null)}
        onPurchase={(p) => { setViewing(null); setPending(p); }}
      />
    </main>
  );
}
